import React from 'react';
import { Option } from 'shared/components/ui/custom-form-select';
import { ButtonVariants } from 'shared/components/ui/popup-menu';

import { DateFormatOptions } from './date-format-options';
import { FilterDocumentDateFilter } from './filter-document';
import { ID } from './id';
import { TaskPriorityOptions, TaskType } from './task-base';
import { Timestamp } from './timestamp';
import { WeekDays } from './week-days';

export enum FilterOptionType {
  Time = 'time',
  Date = 'date',
  DateRange = 'date-range',
  Select = 'select',
  MultiSelect = 'multi-select',
  Checkbox = 'checkbox',
  NumberRange = 'number-range',
  Button = 'button',
}

export type FilterOptionBase = {
  id: ID;
  label: string;
  tooltip?: string;
  children?: FilterGroup[];
  canReset?: boolean;
  icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  infoUrl?: string;
};

export type FilterOptionDefault = FilterOptionBase & {
  type?: Exclude<
    FilterOptionType,
    | FilterOptionType.Select
    | FilterOptionType.MultiSelect
    | FilterOptionType.Checkbox
    | FilterOptionType.NumberRange
    | FilterOptionType.DateRange
    | FilterOptionType.Button
  >;
};

export type FilterSelectOption = FilterOptionBase & {
  type: FilterOptionType.Select | FilterOptionType.MultiSelect;
  selectOptions: Option[];
  placeholder: string;
  recursiveSelection?: boolean;
  enableSearch?: boolean;
  optionsAsButtons?: boolean;
};

export type FilterCheckboxOption = Omit<FilterOptionBase, 'icon'> & {
  type: FilterOptionType.Checkbox;
};

export type FilterNumberRangeOption = FilterOptionBase & {
  type: FilterOptionType.NumberRange;
  minLabel: string;
  maxLabel: string;
  placeholder: string;
};

export type FilterDateRangeOption = FilterOptionBase & {
  type: FilterOptionType.DateRange;
  dateFormat: DateFormatOptions;
  weekStartsOn: WeekDays;
  isDeadline?: boolean;
  hideUndefinedDateOptions?: boolean;
};

export type FilterButtonOption = FilterOptionBase & {
  type: FilterOptionType.Button;
  buttonVariant?: ButtonVariants;
  disabled?: boolean;
};

export type FilterOption =
  | FilterOptionDefault
  | FilterSelectOption
  | FilterCheckboxOption
  | FilterNumberRangeOption
  | FilterDateRangeOption
  | FilterButtonOption;

export type FilterGroup = {
  id: ID;
  title?: string;
  filters: FilterOption[];
  scrollable?: boolean;
};

export type ActiveFilter = {
  id: ID;
  value: string[];
  dateFilter?: FilterDocumentDateFilter;
  isDefault?: boolean;
};

export enum QueryFilterOptionKeys {
  LifeAreas = 'lifeAreas',
  Goals = 'goals',
  Ids = 'ids',
  TasksOnly = 'tasksOnly',
  ShowCompleted = 'showCompleted',
  DeadlineDeadlineStartDate = 'deadlineDeadlineStartDate',
  DeadlineDeadlineEndDate = 'deadlineDeadlineEndDate',
  HasDeadlineDeadline = 'hasDeadlineDeadline',
  NoDeadlineDeadline = 'noDeadlineDeadline',
  DeadlineStartDate = 'deadlineStartDate',
  DeadlineEndDate = 'deadlineEndDate',
  HasDeadline = 'hasDeadline',
  NoDeadline = 'noDeadline',
  NoLifeArea = 'noLifeArea',
  NoGoal = 'noGoal',
  ParentIds = 'parentIds',
  NoParentIds = 'noParentIds',
  CompletedStartDate = 'completedStartDate',
  CompletedEndDate = 'completedEndDate',
}

export type QueryFilterOptions = {
  [QueryFilterOptionKeys.LifeAreas]?: ID[];
  [QueryFilterOptionKeys.Goals]?: ID[];
  [QueryFilterOptionKeys.Ids]?: ID[];
  [QueryFilterOptionKeys.ShowCompleted]?: boolean;
  [QueryFilterOptionKeys.TasksOnly]?: boolean;
  [QueryFilterOptionKeys.DeadlineDeadlineStartDate]?: Timestamp; // timestamp in milliseconds
  [QueryFilterOptionKeys.DeadlineDeadlineEndDate]?: Timestamp; // timestamp in milliseconds
  [QueryFilterOptionKeys.HasDeadlineDeadline]?: boolean;
  [QueryFilterOptionKeys.NoDeadlineDeadline]?: boolean;
  [QueryFilterOptionKeys.DeadlineStartDate]?: Timestamp; // timestamp in milliseconds
  [QueryFilterOptionKeys.DeadlineEndDate]?: Timestamp; // timestamp in milliseconds
  [QueryFilterOptionKeys.HasDeadline]?: boolean;
  [QueryFilterOptionKeys.NoDeadline]?: boolean;
  [QueryFilterOptionKeys.NoLifeArea]?: boolean;
  [QueryFilterOptionKeys.NoGoal]?: boolean;
  [QueryFilterOptionKeys.ParentIds]?: ID[];
  [QueryFilterOptionKeys.NoParentIds]?: boolean;
  [QueryFilterOptionKeys.CompletedStartDate]?: Timestamp; // timestamp in milliseconds
  [QueryFilterOptionKeys.CompletedEndDate]?: Timestamp; // timestamp in milliseconds
};

export type TaskFilterOptions = QueryFilterOptions & {
  types?: TaskType[];
  priorities?: TaskPriorityOptions[];
  minDuration?: number; // in milliseconds (from "more than" filter)
  maxDuration?: number; // in milliseconds (from "less than" filter)
  excludeIds?: ID[];
  includeTaskWithAction?: boolean;
  excludeHiddenTodoHabits?: boolean;
  archivedGoalIds?: ID[];
};
